import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmt, fmtInt, formatApiError } from "@/lib/api";
import { Plus, Search, X, Ban, Pencil } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import Pagination from "@/components/Pagination";

export default function Invoices() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [status, setStatus] = useState("");
  const [cancelling, setCancelling] = useState(null);   // invoice object pending confirmation
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const load = () => api.get("/invoices").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const doCancel = async () => {
    if (!cancelling) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/invoices/${cancelling.id}/cancel`);
      setCancelling(null);
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  };

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return items.filter((inv) => {
          if (q) {
            const styleIds = (inv.items || []).map((it) => it.style_id || "").join(",");
            const hay = `${inv.customer_snapshot?.name || ""} ${inv.invoice_number || ""} ${styleIds}`.toLowerCase();
            if (!hay.includes(q)) return false;
          }
          if (from && (inv.invoice_date || "") < from) return false;
          if (to && (inv.invoice_date || "") > to) return false;
          if (status && (inv.status || "DRAFT") !== status) return false;
          return true;
        });
      }, [items, query, from, to, status]);

  const itemQty = (inv) => (inv.items || []).reduce((a, it) => a + Number(it.qty || 0), 0);

  const totals = useMemo(() => {
    let grand = 0, paid = 0, bal = 0, qty = 0;
    filtered.forEach((i) => {
      grand += Number(i.grand_total || 0);
      paid += Number(i.amount_received || 0);
      bal += Number(i.balance || 0);
      qty += itemQty(i);
    });
    return { grand, paid, bal, qty };
  }, [filtered]);

  const clearFilters = () => { setQuery(""); setFrom(""); setTo(""); setStatus(""); };
  const hasFilters = query || from || to || status;

  useEffect(() => { setPage(1); }, [query, from, to, status, pageSize]);
  const paged = useMemo(() => filtered.slice((page - 1) * pageSize, page * pageSize), [filtered, page, pageSize]);

  return (
    <div data-testid="invoices-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Invoices</h1>
          <p className="page-sub">{filtered.length} of {items.length} invoice(s)</p>
        </div>
        <Link to="/invoices/new" className="btn btn-primary" data-testid="new-invoice-btn"><Plus size={16}/> New Invoice</Link>
      </div>

      <div className="card" style={{ marginBottom: 16 }} data-testid="invoice-filters">
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr auto", gap: 10, alignItems: "end" }}>
          <div className="field">
            <label>Search customer / invoice #</label>
            <div className="search-wrap">
              <Search size={16}/>
              <input data-testid="invoice-search" className="input" placeholder="e.g. Acme Buyer or INV-2026-..." value={query} onChange={(e) => setQuery(e.target.value)}/>
            </div>
          </div>
          <div className="field">
            <label>From</label>
            <input data-testid="invoice-from" className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)}/>
          </div>
          <div className="field">
            <label>To</label>
            <input data-testid="invoice-to" className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)}/>
          </div>
          <div className="field">
            <label>Status</label>
            <select data-testid="invoice-status-filter" className="select" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All</option>
              <option value="DRAFT">Draft</option>
              <option value="BALANCE_PENDING">Balance Pending</option>
              <option value="CLEARED">Cleared</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
          <button className="btn btn-ghost btn-sm" data-testid="clear-filters" onClick={clearFilters} disabled={!hasFilters} style={{ height: 36 }}><X size={14}/> Clear</button>
        </div>
        {hasFilters && (
          <div style={{ marginTop: 12, padding: "10px 14px", background: "#F8FAFC", border: "1px solid var(--border)", borderRadius: 4, fontSize: 13, display: "flex", gap: 24, flexWrap: "wrap" }} data-testid="filter-totals">
            <span>Filtered Total: <strong className="tabular">₹ {fmtInt(Math.round(totals.grand))}</strong></span>
            <span>Paid: <strong className="tabular" style={{ color: "var(--success)" }}>₹ {fmt(totals.paid)}</strong></span>
            <span>Outstanding: <strong className="tabular" style={{ color: totals.bal > 0 ? "var(--danger)" : "var(--text-muted)" }}>₹ {fmt(totals.bal)}</strong></span>
            <span>Total Qty: <strong className="tabular">{fmtInt(totals.qty)}</strong></span>
          </div>
        )}
      </div>

      <div className="card-flush">
        <table className="tbl">
          <thead><tr><th>Invoice #</th><th>Date</th><th>Customer</th><th>Status</th><th>Items</th><th className="num">Grand Total</th><th className="num">Paid</th><th className="num">Balance</th><th className="num">Total Qty</th><th></th></tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={10} style={{ textAlign: "center", color: "var(--text-muted)", padding: 32 }}>{items.length === 0 ? "No invoices yet." : "No invoices match the filters."}</td></tr>}
            {paged.map((inv) => {
              const isCancelled = (inv.status || "DRAFT") === "CANCELLED";
              return (
                <tr key={inv.id} data-testid={`invoice-row-${inv.invoice_number}`} style={isCancelled ? { opacity: 0.55 } : {}}>
                  <td><Link to={`/invoices/${inv.id}`} style={{ color: "var(--accent)", textDecoration: "none", fontWeight: 600 }}>{inv.invoice_number}</Link></td>
                  <td>{inv.invoice_date}</td>
                  <td>{inv.customer_snapshot?.name}{inv.is_defect && <span style={{ marginLeft: 6, fontSize: 11, padding: "2px 6px", background: "#FEF3C7", color: "#92400E", borderRadius: 3 }}>DEFECT</span>}</td>
                  <td><StatusBadge status={inv.status || "DRAFT"}/></td>
                  <td>{inv.items?.length}</td>
                  <td className="num"><strong>₹ {fmtInt(Math.round(inv.grand_total || 0))}</strong></td>
                  <td className="num">₹ {fmt(inv.amount_received || 0)}</td>
                  <td className="num" style={{ color: (inv.balance || 0) > 0 && !isCancelled ? "var(--danger)" : "var(--text-muted)", fontWeight: (inv.balance || 0) > 0 && !isCancelled ? 600 : 400 }}>₹ {fmt(inv.balance || 0)}</td>
                  <td className="num">{fmtInt(itemQty(inv))}</td>
                  <td>
                    <div style={{ display: "flex", gap: 6 }}>
                      {(inv.status === "DRAFT" || inv.status === "BALANCE_PENDING") && (
                        <button
                          className="btn btn-secondary btn-sm"
                          data-testid={`edit-invoice-${inv.invoice_number}`}
                          onClick={() => nav(`/invoices/${inv.id}/edit`)}
                          title="Edit invoice"
                        >
                          <Pencil size={14}/> Edit
                        </button>
                      )}
                      <button
                        className="btn btn-danger btn-sm"
                        data-testid={`cancel-invoice-${inv.invoice_number}`}
                        onClick={() => { setErr(""); setCancelling(inv); }}
                        disabled={isCancelled}
                        title={isCancelled ? "Already cancelled" : "Cancel invoice and restore stock"}
                      >
                        <Ban size={14}/> Cancel
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Pagination page={page} pageSize={pageSize} total={filtered.length} onPageChange={setPage} onPageSizeChange={setPageSize}/>
      </div>

      {cancelling && (
        <div className="modal-bg" onClick={() => !busy && setCancelling(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="cancel-invoice-modal" style={{ maxWidth: 460 }}>
            <div className="modal-h"><h2>Cancel Invoice</h2><button className="btn btn-ghost btn-sm" onClick={() => !busy && setCancelling(null)}><X size={16}/></button></div>
            <div className="modal-b">
              {err && <div className="error-msg" style={{ marginBottom: 12 }}>{err}</div>}
              <p style={{ marginBottom: 10 }}>Are you sure you want to cancel <strong>{cancelling.invoice_number}</strong> for <strong>{cancelling.customer_snapshot?.name}</strong>?</p>
              <ul style={{ marginLeft: 18, color: "var(--text-muted)", fontSize: 13, lineHeight: 1.7 }}>
                <li>The invoice will be marked as <strong>Cancelled</strong>.</li>
                <li>Stock for all line items will be <strong>restored</strong>.</li>
                <li>Payment records will be retained for history but excluded from sales totals.</li>
                <li>This action cannot be undone.</li>
              </ul>
            </div>
            <div className="modal-f">
              <button className="btn btn-secondary" onClick={() => setCancelling(null)} disabled={busy}>Keep Invoice</button>
              <button className="btn btn-danger" data-testid="confirm-cancel-invoice" onClick={doCancel} disabled={busy}>{busy ? "Cancelling..." : "Cancel & Restore Stock"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
